import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Plus, Mail, Trash2, RefreshCw, Shield, Crown } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { usePortalAuth, PortalPermission } from '@/hooks/usePortalAuth';
import { useReadOnly } from '@/contexts/ReadOnlyContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';

interface TeamMember {
  id: string;
  userId: string | null;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  role: 'owner' | 'staff';
  status: 'active' | 'pending';
  permissions: PortalPermission[];
}

const PERMISSION_LABELS: Record<PortalPermission, { label: string; description: string }> = {
  view_earnings: { label: 'View Earnings', description: 'Can see commission data and earnings page' },
  view_team: { label: 'View Team', description: 'Can see team members list (read-only)' },
  manage_settings: { label: 'Manage Settings', description: 'Can access and modify company settings' },
  order_medallions: { label: 'Order Medallions', description: 'Can place medallion orders and view order history' },
};

const PortalTeam = () => {
  const navigate = useNavigate();
  const { company, isOwner, hasPermission, isLoading: authLoading } = usePortalAuth();
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<TeamMember | null>(null);
  const [resendingEmail, setResendingEmail] = useState<string | null>(null);
  const [savingPermissions, setSavingPermissions] = useState<string | null>(null);
  const [permissionsMember, setPermissionsMember] = useState<TeamMember | null>(null);
  const [memberToTransferTo, setMemberToTransferTo] = useState<TeamMember | null>(null);
  const [isTransferring, setIsTransferring] = useState(false);
  const [transferConfirmation, setTransferConfirmation] = useState('');
  
  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [newMemberPermissions, setNewMemberPermissions] = useState<PortalPermission[]>([]);

  // Permission check - redirect if no access
  useEffect(() => {
    if (!authLoading && !isOwner && !hasPermission('view_team')) {
      toast.error('You do not have permission to view the team');
      navigate('/portal/dashboard');
    }
  }, [authLoading, isOwner, hasPermission, navigate]);

  const fetchTeamMembers = useCallback(async () => {
    if (!company?.id) return;

    setIsLoading(true);
    try {
      // Fetch monument company users
      const { data: companyUsers, error: usersError } = await supabase
        .from('monument_company_users')
        .select('id, user_id, invited_email, invited_first_name, invited_last_name, invited_phone, role')
        .eq('company_id', company.id);

      if (usersError) throw usersError;

      // Fetch permissions for all company users
      const companyUserIds = companyUsers?.map(u => u.id) || [];
      let permissionsMap: Record<string, PortalPermission[]> = {};
      
      if (companyUserIds.length > 0) {
        const { data: permissions, error: permError } = await supabase
          .from('monument_company_user_permissions')
          .select('company_user_id, permission')
          .in('company_user_id', companyUserIds);

        if (permError) {
          console.error('Error fetching permissions:', permError);
        } else {
          permissionsMap = (permissions || []).reduce((acc, p) => {
            if (!acc[p.company_user_id]) acc[p.company_user_id] = [];
            acc[p.company_user_id].push(p.permission as PortalPermission);
            return acc;
          }, {} as Record<string, PortalPermission[]>);
        }
      }

      // Transform to team members
      const members: TeamMember[] = (companyUsers || []).map(user => {
        return {
          id: user.id,
          userId: user.user_id,
          firstName: user.invited_first_name || '',
          lastName: user.invited_last_name || '',
          email: user.invited_email || '',
          phone: user.invited_phone,
          role: user.role as 'owner' | 'staff',
          status: user.user_id ? 'active' : 'pending',
          permissions: permissionsMap[user.id] || [],
        };
      });

      // Sort: owners first, then by name
      members.sort((a, b) => {
        if (a.role !== b.role) return a.role === 'owner' ? -1 : 1;
        return `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
      });

      setTeamMembers(members);
    } catch (error) {
      console.error('Error fetching team members:', error);
      toast.error('Failed to load team members');
    } finally {
      setIsLoading(false);
    }
  }, [company?.id]);

  useEffect(() => {
    if (company?.id) {
      fetchTeamMembers();
    }
  }, [company?.id, fetchTeamMembers]);

  const resetForm = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setNewMemberPermissions([]);
  };

  const handleAddStaff = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!company?.id) return;
    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    try {
      // Check if email is already on this team
      const { data: existingTeamMember } = await supabase
        .from('monument_company_users')
        .select('id')
        .eq('company_id', company.id)
        .ilike('invited_email', email)
        .single();

      if (existingTeamMember) {
        toast.error('This email is already on your team');
        setIsSubmitting(false);
        return;
      }

      // Check if this email belongs to an existing auth user (avoids orphaned admins records)
      const { data: emailCheck } = await supabase.functions.invoke('check-email-exists', {
        body: { email: email.toLowerCase() }
      });

      const existingUserId = emailCheck?.userId || null;
      let newUserId: string | null = null;

      if (existingUserId) {
        // EXISTING USER: Insert with user_id already set (immediately linked)
        const { data: newUser, error: insertError } = await supabase
          .from('monument_company_users')
          .insert({
            company_id: company.id,
            user_id: existingUserId,
            invited_email: email.toLowerCase(),
            invited_first_name: firstName.trim(),
            invited_last_name: lastName.trim(),
            invited_phone: phone.trim() || null,
            role: 'staff',
          })
          .select('id')
          .single();

        if (insertError) throw insertError;
        newUserId = newUser?.id || null;

        // Send welcome email (not signup invitation)
        const { error: emailError } = await supabase.functions.invoke('send-monument-company-invite', {
          body: {
            email: email.toLowerCase(),
            companyName: company.name,
            companyId: company.id,
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            role: 'staff',
            existingUser: true,
          },
        });

        if (emailError) {
          console.error('Error sending welcome email:', emailError);
          toast.warning('Staff added but welcome email failed to send');
        } else {
          toast.success('Staff member added successfully');
        }
      } else {
        // NEW USER: Insert with user_id = null (pending invitation)
        const { data: newUser, error: insertError } = await supabase
          .from('monument_company_users')
          .insert({
            company_id: company.id,
            invited_email: email.toLowerCase(),
            invited_first_name: firstName.trim(),
            invited_last_name: lastName.trim(),
            invited_phone: phone.trim() || null,
            role: 'staff',
          })
          .select('id')
          .single();

        if (insertError) throw insertError;
        newUserId = newUser?.id || null;

        // Send signup invitation email
        const { error: emailError } = await supabase.functions.invoke('send-monument-company-invite', {
          body: {
            email: email.toLowerCase(),
            companyName: company.name,
            companyId: company.id,
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            role: 'staff',
          },
        });

        if (emailError) {
          console.error('Error sending invitation email:', emailError);
          toast.warning('Staff added but invitation email failed to send');
        } else {
          toast.success('Staff member invited successfully');
        }
      }

      // Insert permissions if any were selected
      if (newMemberPermissions.length > 0 && newUserId) {
        const { error: permError } = await supabase
          .from('monument_company_user_permissions')
          .insert(
            newMemberPermissions.map(perm => ({
              company_user_id: newUserId,
              permission: perm,
            }))
          );
        
        if (permError) {
          console.error('Error adding permissions:', permError);
        }
      }

      // Sync to linked fulfillment partner if company has one
      if (company.linked_fulfillment_partner_id) {
        await supabase.rpc('sync_monument_users_to_partner', {
          _company_id: company.id,
          _partner_id: company.linked_fulfillment_partner_id,
        });
      }

      resetForm();
      setIsAddDialogOpen(false);
      fetchTeamMembers();
    } catch (error) {
      console.error('Error adding staff:', error);
      toast.error('Failed to add staff member');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendInvite = async (member: TeamMember) => {
    if (!company?.id || member.status !== 'pending') return;

    setResendingEmail(member.id);
    try {
      const { error } = await supabase.functions.invoke('send-monument-company-invite', {
        body: {
          email: member.email,
          companyName: company.name,
          companyId: company.id,
          firstName: member.firstName,
          lastName: member.lastName,
          role: member.role,
        },
      });

      if (error) throw error;
      toast.success('Invitation email resent');
    } catch (error) {
      console.error('Error resending invite:', error);
      toast.error('Failed to resend invitation');
    } finally {
      setResendingEmail(null);
    }
  };

  const handleRemoveStaff = async () => {
    if (!memberToRemove) return;

    try {
      const { error } = await supabase
        .from('monument_company_users')
        .delete()
        .eq('id', memberToRemove.id);

      if (error) throw error;

      toast.success('Staff member removed');
      setMemberToRemove(null);
      fetchTeamMembers();
    } catch (error) {
      console.error('Error removing staff:', error);
      toast.error('Failed to remove staff member');
    }
  };

  const handleTogglePermission = async (member: TeamMember, permission: PortalPermission) => {
    if (!isOwner) return;
    
    setSavingPermissions(member.id);
    const hasPermissionNow = member.permissions.includes(permission);
    
    try {
      if (hasPermissionNow) {
        // Remove permission
        const { error } = await supabase
          .from('monument_company_user_permissions')
          .delete()
          .eq('company_user_id', member.id)
          .eq('permission', permission);
        
        if (error) throw error;
      } else {
        // Add permission
        const { error } = await supabase
          .from('monument_company_user_permissions')
          .insert({
            company_user_id: member.id,
            permission: permission,
          });
        
        if (error) throw error;
      }
      
      // Update local state
      const updatedPermissions = hasPermissionNow
        ? member.permissions.filter(p => p !== permission)
        : [...member.permissions, permission];
        
      setTeamMembers(prev => prev.map(m => {
        if (m.id === member.id) {
          return { ...m, permissions: updatedPermissions };
        }
        return m;
      }));
      
      // Also update the dialog state if this member is open
      setPermissionsMember(prev => {
        if (prev && prev.id === member.id) {
          return { ...prev, permissions: updatedPermissions };
        }
        return prev;
      });
      
      toast.success(`Permission ${hasPermissionNow ? 'removed' : 'granted'}`);
    } catch (error) {
      console.error('Error updating permission:', error);
      toast.error('Failed to update permission');
    } finally {
      setSavingPermissions(null);
    }
  };

  const handleTransferOwnership = async () => {
    if (!memberToTransferTo || !company?.id) return;
    
    // Validate confirmation text
    if (transferConfirmation !== 'TRANSFER OWNERSHIP') {
      toast.error('Please type "TRANSFER OWNERSHIP" to confirm');
      return;
    }

    setIsTransferring(true);
    try {
      // Find current owner
      const currentOwner = teamMembers.find(m => m.role === 'owner');
      if (!currentOwner) {
        toast.error('Current owner not found');
        return;
      }

      // Start transaction-like updates
      // 1. Change current owner to staff
      const { error: downgradeError } = await supabase
        .from('monument_company_users')
        .update({ role: 'staff' })
        .eq('id', currentOwner.id);

      if (downgradeError) throw downgradeError;

      // 2. Change new member to owner
      const { error: upgradeError } = await supabase
        .from('monument_company_users')
        .update({ role: 'owner' })
        .eq('id', memberToTransferTo.id);

      if (upgradeError) {
        // Rollback: restore current owner
        await supabase
          .from('monument_company_users')
          .update({ role: 'owner' })
          .eq('id', currentOwner.id);
        throw upgradeError;
      }

      // 3. Remove all permissions from new owner (owners don't need explicit permissions)
      await supabase
        .from('monument_company_user_permissions')
        .delete()
        .eq('company_user_id', memberToTransferTo.id);

      toast.success(`Ownership successfully transferred to ${memberToTransferTo.firstName} ${memberToTransferTo.lastName}`);
      
      // Reset states and refresh
      setMemberToTransferTo(null);
      setTransferConfirmation('');
      fetchTeamMembers();
      
      // Note: This will cause the user to lose owner privileges, so they might get redirected
    } catch (error) {
      console.error('Error transferring ownership:', error);
      toast.error('Failed to transfer ownership. Please try again.');
    } finally {
      setIsTransferring(false);
    }
  };

  // Check if user can manage team (owners only, not when deactivated)
  const { isReadOnly } = useReadOnly();
  const canManage = isOwner && !isReadOnly;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-10 w-28" />
        </div>
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Team</h1>
          <p className="text-muted-foreground mt-1">
            {canManage ? "Manage your company's staff members" : "View your company's team members"}
          </p>
        </div>
        {canManage && (
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Add Staff
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[85vh] flex flex-col">
              <form onSubmit={handleAddStaff} className="flex flex-col flex-1 overflow-hidden">
                <DialogHeader>
                  <DialogTitle>Add Staff Member</DialogTitle>
                  <DialogDescription>
                    Invite a new staff member to join your company on Pontis.
                  </DialogDescription>
                </DialogHeader>
                <div className="flex-1 overflow-y-auto py-4">
                  <div className="grid gap-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">First Name <span className="required-asterisk">*</span></Label>
                      <Input
                        id="firstName"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder="John"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Last Name <span className="required-asterisk">*</span></Label>
                      <Input
                        id="lastName"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        placeholder="Doe"
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email <span className="required-asterisk">*</span></Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="john@example.com"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone (Optional)</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  
                  <div className="border-t pt-4">
                    <Label className="text-sm font-medium">Permissions</Label>
                    <p className="text-sm text-muted-foreground mb-3">Set what this staff member can access</p>
                    <div className="space-y-3">
                      {(Object.keys(PERMISSION_LABELS) as PortalPermission[]).map((perm) => (
                        <div key={perm} className="flex items-start gap-3 p-3 rounded-lg border">
                          <Checkbox
                            id={`new-${perm}`}
                            checked={newMemberPermissions.includes(perm)}
                            onCheckedChange={(checked) => {
                              setNewMemberPermissions(prev =>
                                checked
                                  ? [...prev, perm]
                                  : prev.filter(p => p !== perm)
                              );
                            }}
                          />
                          <div className="flex-1">
                            <Label htmlFor={`new-${perm}`} className="font-medium cursor-pointer">
                              {PERMISSION_LABELS[perm].label}
                            </Label>
                            <p className="text-sm text-muted-foreground">
                              {PERMISSION_LABELS[perm].description}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      resetForm();
                      setIsAddDialogOpen(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Sending...' : 'Send Invitation'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Stats Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total Team Members</CardTitle>
          <Users className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{teamMembers.length}</div>
          <p className="text-xs text-muted-foreground">
            {teamMembers.filter(m => m.status === 'active').length} active, {teamMembers.filter(m => m.status === 'pending').length} pending
          </p>
        </CardContent>
      </Card>

      {/* Team Table */}
      <Card>
        <CardHeader>
          <CardTitle>Team Members</CardTitle>
          <CardDescription>
            {canManage 
              ? 'All staff members who have access to your company portal'
              : 'Team members in your company'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {teamMembers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No team members yet. {canManage && 'Add your first staff member.'}
            </div>
          ) : (
            <>
              {/* Mobile Card Layout */}
              <div className="md:hidden space-y-4">
                {teamMembers.map((member) => (
                  <div
                    key={member.id}
                    className="border rounded-lg p-4 space-y-3"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="font-medium text-foreground">
                          {member.firstName} {member.lastName}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {member.email}
                        </div>
                        {member.phone && (
                          <div className="text-sm text-muted-foreground">
                            {member.phone}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-1 items-end">
                        <Badge variant={member.role === 'owner' ? 'default' : 'secondary'}>
                          {member.role === 'owner' ? 'Owner' : 'Staff'}
                        </Badge>
                        <Badge variant={member.status === 'active' ? 'outline' : 'secondary'}>
                          {member.status === 'active' ? 'Active' : 'Pending'}
                        </Badge>
                      </div>
                    </div>
                    
                    {/* Permissions button for staff (owners only) */}
                    {canManage && member.role === 'staff' && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="w-full justify-start"
                        onClick={() => setPermissionsMember(member)}
                      >
                        <Shield className="h-4 w-4 mr-2" />
                        Manage Permissions ({member.permissions.length})
                      </Button>
                    )}
                    
                    {/* Transfer ownership button for active staff (owners only) */}
                    {canManage && member.role === 'staff' && member.status === 'active' && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="w-full justify-start text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                        onClick={() => setMemberToTransferTo(member)}
                      >
                        <Crown className="h-4 w-4 mr-2" />
                        Transfer Ownership
                      </Button>
                    )}
                    
                    {canManage && (member.status === 'pending' || member.role === 'staff') && (
                      <div className="flex gap-2 pt-2 border-t">
                        {member.status === 'pending' && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1"
                            onClick={() => handleResendInvite(member)}
                            disabled={resendingEmail === member.id}
                          >
                            {resendingEmail === member.id ? (
                              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Mail className="h-4 w-4 mr-2" />
                            )}
                            Resend Invite
                          </Button>
                        )}
                        {member.role === 'staff' && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1 text-destructive hover:text-destructive border-destructive/50 hover:bg-destructive/10"
                            onClick={() => setMemberToRemove(member)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Remove
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Desktop Table Layout */}
              <div className="hidden md:block overflow-x-auto -mx-6 px-6">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Status</TableHead>
                      {canManage && <TableHead>Permissions</TableHead>}
                      {canManage && <TableHead className="w-[100px]">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {teamMembers.map((member) => (
                      <TableRow key={member.id}>
                        <TableCell className="font-medium">
                          {member.firstName} {member.lastName}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {member.email}
                        </TableCell>
                        <TableCell>
                          <Badge variant={member.role === 'owner' ? 'default' : 'secondary'}>
                            {member.role === 'owner' ? 'Owner' : 'Staff'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={member.status === 'active' ? 'outline' : 'secondary'}>
                            {member.status === 'active' ? 'Active' : 'Pending'}
                          </Badge>
                        </TableCell>
                        {canManage && (
                          <TableCell>
                            {member.role === 'owner' ? (
                              <span className="text-sm text-muted-foreground">All permissions</span>
                            ) : (
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => setPermissionsMember(member)}
                              >
                                <Shield className="h-3 w-3 mr-1" />
                                {member.permissions.length} permission{member.permissions.length !== 1 ? 's' : ''}
                              </Button>
                            )}
                          </TableCell>
                        )}
                        {canManage && (
                          <TableCell>
                            <div className="flex items-center gap-1">
                              {member.status === 'pending' && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => handleResendInvite(member)}
                                  disabled={resendingEmail === member.id}
                                  title="Resend invitation"
                                >
                                  {resendingEmail === member.id ? (
                                    <RefreshCw className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Mail className="h-4 w-4" />
                                  )}
                                </Button>
                              )}
                              {member.role === 'staff' && member.status === 'active' && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                  onClick={() => setMemberToTransferTo(member)}
                                  title="Transfer ownership"
                                >
                                  <Crown className="h-4 w-4" />
                                </Button>
                              )}
                              {member.role === 'staff' && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-destructive hover:text-destructive"
                                  onClick={() => setMemberToRemove(member)}
                                  title="Remove staff member"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Permissions Dialog */}
      <Dialog 
        open={!!permissionsMember} 
        onOpenChange={(open) => !open && setPermissionsMember(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manage Permissions</DialogTitle>
            <DialogDescription>
              Set access levels for {permissionsMember?.firstName} {permissionsMember?.lastName}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            {(Object.keys(PERMISSION_LABELS) as PortalPermission[]).map((perm) => (
              <div key={perm} className="flex items-start gap-3 p-3 rounded-lg border">
                <Checkbox
                  id={`dialog-${perm}`}
                  checked={permissionsMember?.permissions.includes(perm)}
                  onCheckedChange={() => {
                    if (permissionsMember) {
                      handleTogglePermission(permissionsMember, perm);
                    }
                  }}
                  disabled={savingPermissions === permissionsMember?.id}
                />
                <div className="flex-1">
                  <Label htmlFor={`dialog-${perm}`} className="font-medium cursor-pointer">
                    {PERMISSION_LABELS[perm].label}
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    {PERMISSION_LABELS[perm].description}
                  </p>
                </div>
              </div>
            ))}
          </div>
          
          <DialogFooter>
            <Button onClick={() => setPermissionsMember(null)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Confirmation Dialog */}
      <AlertDialog open={!!memberToRemove} onOpenChange={(open) => !open && setMemberToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Staff Member</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove {memberToRemove?.firstName} {memberToRemove?.lastName} from your team? 
              They will lose access to the portal immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveStaff}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Transfer Ownership Confirmation Dialog */}
      <Dialog 
        open={!!memberToTransferTo} 
        onOpenChange={(open) => {
          if (!open) {
            setMemberToTransferTo(null);
            setTransferConfirmation('');
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <Crown className="h-5 w-5" />
              Transfer Portal Ownership
            </DialogTitle>
            <DialogDescription>
              This action will transfer complete ownership of the portal to{' '}
              <span className="font-medium">
                {memberToTransferTo?.firstName} {memberToTransferTo?.lastName}
              </span>
              .
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-4">
              <h4 className="font-medium text-amber-800 mb-2">⚠️ Important Notes:</h4>
              <ul className="text-sm text-amber-700 space-y-1">
                <li>• You will lose owner privileges immediately</li>
                <li>• The new owner will have full control of the portal</li>
                <li>• You will become a regular staff member</li>
                <li>• This action cannot be undone without the new owner's consent</li>
              </ul>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirm-transfer" className="text-sm font-medium">
                Type "TRANSFER OWNERSHIP" to confirm this action:
              </Label>
              <Input
                id="confirm-transfer"
                value={transferConfirmation}
                onChange={(e) => setTransferConfirmation(e.target.value)}
                placeholder="TRANSFER OWNERSHIP"
                className="font-mono"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setMemberToTransferTo(null);
                setTransferConfirmation('');
              }}
              disabled={isTransferring}
            >
              Cancel
            </Button>
            <Button
              onClick={handleTransferOwnership}
              disabled={isTransferring || transferConfirmation !== 'TRANSFER OWNERSHIP'}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {isTransferring ? 'Transferring...' : 'Transfer Ownership'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PortalTeam;
